import { STORE_SCREEN } from 'utils/storeScreen';
import { getCurrentAuthToken } from './authHelpers';
import { getLocalAppSession } from './storageHelpers';

/**
 * Sends a save that survives the tab going away.
 *
 * Apollo's request is cancelled the moment the page unloads, so the last words
 * typed inside the quiet pause would be lost. `keepalive` hands the request to
 * the browser, which finishes it after the page is gone. Nothing can be
 * reported back: by then there is no screen left to report to.
 *
 * `basedOn` is the count the screen is built on (D10). With it the server
 * refuses this store when someone else changed the thing meanwhile: the
 * last two seconds of typing are then dropped unseen, and the colleague's
 * work stays (Steven, 6 Oct 2026; before, such a store went without a count
 * on most screens and overwrote the colleague's work unseen).
 */
export const sendKeepaliveSave = (
  path: string,
  body: unknown,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  basedOn?: { version: number | null; overwrite?: boolean }
): void => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';

  try {
    void fetch(`${base}${path}`, {
      method,
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
        'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
      },
      // With the screen's name (D10): this store's answer is never heard,
      // and it must not make its own screen out of date.
      body: JSON.stringify(
        body !== null && typeof body === 'object' && !Array.isArray(body)
          ? {
              ...(body as Record<string, unknown>),
              store_screen: STORE_SCREEN,
              ...(basedOn && basedOn.version !== null
                ? { based_on: basedOn.version }
                : {}),
              ...(basedOn?.overwrite ? { overwrite: true } : {}),
            }
          : body
      ),
    });
  } catch (_e) {
    // The tab is closing. There is nowhere to show an error.
  }
};
